import { AppCard, AppPage } from '../../components/general';
import { FC, useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { useForm } from 'react-hook-form';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { confirmPopup, ConfirmPopup } from 'primereact/confirmpopup';
import { FormInputText, FormDropdown } from '../../components/form';
import { FeeRuleEditorDialog } from './FeeRuleEditorDialog';
import {
  FeeConfigurationViewModel,
  FeeHudLineViewModel,
  FeeRuleViewModel,
  FeeRuleType,
  CreateOrUpdateFeeConfigRequestViewModel,
  FeeRulesVersionViewModel,
} from '../../models/fees';
import { formatFeeVersionDisplay, formatHudLineLabel } from '../../utils/fee-helper';
import { ExpressionPreview } from '../../components/expression/ExpressionPreview';
import { ExpressionDialog } from '../../components/expression/ExpressionDialog';
import { SimpleExpression } from '../../models/expression';

interface FeeConfigurationDetailsProps {
  initialData: FeeConfigurationViewModel | null;
  allFeeConfigurations: FeeConfigurationViewModel[];
  hudLines: FeeHudLineViewModel[];
  versions: FeeRulesVersionViewModel[];
  saveFeeConfiguration: (payload: CreateOrUpdateFeeConfigRequestViewModel) => Promise<FeeConfigurationViewModel>;
  isCreateMode: boolean;
  versionIdFromQuery?: number | null;
}

type FeeConfigurationFormData = {
  description: string | null;
  feeHudLineId: number;
  rules: FeeRuleViewModel[] | null;
};

export const FeeConfigurationDetails: FC<FeeConfigurationDetailsProps> = ({ 
  initialData,
  allFeeConfigurations,
  hudLines, 
  versions,
  saveFeeConfiguration,
  isCreateMode,
  versionIdFromQuery
}) => {
  const navigate = useNavigate();
  
  const contextVersionId = versionIdFromQuery ?? initialData?.feeRulesVersionId;
  const toast = useRef<Toast>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedRule, setSelectedRule] = useState<FeeRuleViewModel | null>(null);
  const [isRuleDialogVisible, setIsRuleDialogVisible] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [selectedExpression, setSelectedExpression] = useState<SimpleExpression | null>(null);
  const [isExpressionDialogVisible, setIsExpressionDialogVisible] = useState(false);
  
  const isReadOnly = !isCreateMode && !!initialData?.feeRulesVersionId;

  // initialData may be non-null in create mode too (copying an existing config seeds the
  // new draft with its data); isCreateMode only controls save behavior (create vs update) and
  // read-only-ness, not whether there's data to seed the form with.
  const initialValues = useRef<FeeConfigurationFormData>({
    description: initialData ? initialData.description : null,
    feeHudLineId: initialData ? initialData.feeHudLineId : (hudLines[0]?.feeHudLineId || 0),
    rules: initialData ? initialData.rules : null,
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
    reset,
  } = useForm<FeeConfigurationFormData>({
    defaultValues: initialValues.current,
  });

  const formDescription = watch('description');
  const formHudLineId = watch('feeHudLineId');
  const formRules = watch('rules') || [];

  const isFormValid = useMemo(() => {
    return formRules && formRules.length > 0;
  }, [formRules]);

  useEffect(() => {
    if (initialData) {
      const newValues: FeeConfigurationFormData = {
        description: initialData.description,
        feeHudLineId: initialData.feeHudLineId,
        rules: initialData.rules,
      };

      initialValues.current = newValues;
      reset(newValues);
      setHasUnsavedChanges(false);
    }
  }, [initialData?.feeConfigurationId, isCreateMode, reset]);

  useEffect(() => {
    if (isReadOnly) {
      setHasUnsavedChanges(false);
      return;
    }

    const descriptionChanged = formDescription !== initialValues.current.description;
    const hudLineChanged = formHudLineId !== initialValues.current.feeHudLineId;
    const rulesChanged = JSON.stringify(formRules) !== JSON.stringify(initialValues.current.rules);

    setHasUnsavedChanges(descriptionChanged || hudLineChanged || rulesChanged);
  }, [formDescription, formHudLineId, formRules, isReadOnly]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleConfirmNavigation = () => {
    setShowUnsavedDialog(false);
    setHasUnsavedChanges(false);
    navigate(contextVersionId ? `/fees?versionId=${contextVersionId}` : '/fees');
  };

  const handleCancelNavigation = () => {
    setShowUnsavedDialog(false);
  };

  const hudLineOptions = useMemo(() => {
    return hudLines
      .map((hudLine) => ({
        label: formatHudLineLabel(hudLine),
        value: hudLine.feeHudLineId,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [hudLines]);

  const hudLineMap = useMemo(() => {
    const map = new Map<number, FeeHudLineViewModel>();
    hudLines.forEach((hudLine) => map.set(hudLine.feeHudLineId, hudLine));
    return map;
  }, [hudLines]);

  const feeNavigationOptions = useMemo(() => {
    return allFeeConfigurations
      .map((config) => {
        const hudLine = hudLineMap.get(config.feeHudLineId);
        const hudLineText = hudLine?.hudLine || '';
        const description = config.description || 'Untitled';
        
        return {
          label: `${hudLineText ? `${hudLineText} - ` : ''}${description}`,
          value: config.feeConfigurationId,
          searchText: `${description} ${hudLineText}`.toLowerCase(),
          hudLineText: hudLineText || 'zzz', // 'zzz' ensures items without hudLine sort last
        };
      })
      .sort((a, b) => {
        return a.hudLineText.localeCompare(b.hudLineText, undefined, { numeric: true, sensitivity: 'base' });
      });
  }, [allFeeConfigurations, hudLineMap]);

  const handleOpenRuleDialog = (rule?: FeeRuleViewModel) => {
    setSelectedRule(rule || null);
    setIsRuleDialogVisible(true);
  };

  const handleCloseRuleDialog = () => {
    setIsRuleDialogVisible(false);
    setSelectedRule(null);
  };

  const handleSaveRule = async (updatedRule: FeeRuleViewModel) => {
    const currentRules = watch('rules') || [];
    let updatedRules: FeeRuleViewModel[];

    if (selectedRule && selectedRule.ruleId) {
      updatedRules = currentRules.map((r) => (r.ruleId === selectedRule.ruleId ? updatedRule : r));
    } else {
      const newRule: FeeRuleViewModel = {
        ...updatedRule,
        ruleId: `temp-${Date.now()}`,
      };
      updatedRules = [...currentRules, newRule];
    }

    setValue('rules', updatedRules);

    try {
      setIsSaving(true);

      const cleanedRules = updatedRules.map((rule) => {
        if (rule.ruleId && rule.ruleId.startsWith('temp-')) {
          const { ruleId, ...ruleWithoutId } = rule;
          return ruleWithoutId as FeeRuleViewModel;
        }
        return rule;
      });

      const payload: CreateOrUpdateFeeConfigRequestViewModel = {
        feeHudLineId: watch('feeHudLineId'),
        description: watch('description'),
        rules: cleanedRules,
      };

      const updatedConfig = await saveFeeConfiguration(payload);

      initialValues.current = {
        description: updatedConfig.description,
        feeHudLineId: updatedConfig.feeHudLineId,
        rules: updatedConfig.rules,
      };

      reset({
        description: updatedConfig.description,
        feeHudLineId: updatedConfig.feeHudLineId,
        rules: updatedConfig.rules,
      });

      setHasUnsavedChanges(false);
      handleCloseRuleDialog();

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Fee configuration saved successfully',
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error instanceof Error ? error.message : 'Failed to save fee configuration',
        life: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyRule = (rule: FeeRuleViewModel) => {
    const clonedRule: FeeRuleViewModel = JSON.parse(JSON.stringify(rule));
    clonedRule.ruleId = `temp-${Date.now()}`;
    clonedRule.description = rule.description ? `${rule.description} (Copy)` : '(Copy)';
    const currentRules = watch('rules') || [];
    setValue('rules', [...currentRules, clonedRule]);
  };

  const handleDeleteRule = (event: React.MouseEvent<HTMLButtonElement>, ruleId: string) => {
    confirmPopup({
      target: event.currentTarget as HTMLElement,
      message: 'Are you sure you want to delete this rule?',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger p-button-sm',
      accept: () => {
        const currentRules = watch('rules') || [];
        const updatedRules = currentRules.filter((r) => r.ruleId !== ruleId);
        setValue('rules', updatedRules);
      },
    });
  };

  const onSubmit = async (data: FeeConfigurationFormData) => {
    setIsSaving(true);
    try {
      const cleanedRules = data.rules?.map((rule) => {
        if (rule.ruleId && rule.ruleId.startsWith('temp-')) {
          const { ruleId, ...ruleWithoutId } = rule;
          return ruleWithoutId as FeeRuleViewModel;
        }
        return rule;
      }) || null;

      const payload: CreateOrUpdateFeeConfigRequestViewModel = {
        feeHudLineId: data.feeHudLineId,
        description: data.description,
        rules: cleanedRules,
      };
      
      const updatedConfig = await saveFeeConfiguration(payload);
      
      initialValues.current = {
        description: updatedConfig.description,
        feeHudLineId: updatedConfig.feeHudLineId,
        rules: updatedConfig.rules,
      };
      
      reset({
        description: updatedConfig.description,
        feeHudLineId: updatedConfig.feeHudLineId,
        rules: updatedConfig.rules,
      });
      
      setHasUnsavedChanges(false);
      
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Fee configuration saved successfully',
        life: 3000,
      });

      setTimeout(() => {
        navigate(contextVersionId ? `/fees?versionId=${contextVersionId}` : '/fees');
      }, 1000);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error instanceof Error ? error.message : 'Failed to save fee configuration',
        life: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      navigate(contextVersionId ? `/fees?versionId=${contextVersionId}` : '/fees');
    }
  };

  const handleDone = () => {
    navigate(contextVersionId ? `/fees?versionId=${contextVersionId}` : '/fees');
  };

  const pageTitle = isCreateMode 
    ? 'Create Fee Configuration' 
    : isReadOnly 
      ? 'View Fee Configuration Details' 
      : 'Edit Fee Configuration Details';
  const breadcrumbItems = [
    { label: 'Dashboard', to: '/dashboard' },
    { label: 'Fees', to: '/products/fees' },
    { label: 'Fee Configurations', to: '/fees' },
    { label: isCreateMode ? 'Create' : isReadOnly ? 'View' : 'Edit' },
  ];
  const versionDisplay = isCreateMode ? 'Draft' : formatFeeVersionDisplay(initialData!, versions);

  return (
    <AppPage title={pageTitle} breadcrumbs={breadcrumbItems}>
      <Toast ref={toast} />
      <ConfirmPopup />
      <AppCard>
        <div className="flex justify-content-between align-items-center mb-3">
          <div className="flex align-items-center gap-2">
            <Tag value={versionDisplay} severity={isReadOnly ? 'success' : 'warning'} />
          </div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-column gap-4">
            {!isCreateMode && (
              <div style={{ width: '375px' }}>
                <label className="font-medium block mb-2">Configuration</label>
                <Dropdown
                  value={initialData?.feeConfigurationId}
                  options={feeNavigationOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => {
                    if (e.value && e.value !== initialData?.feeConfigurationId) {
                      if (hasUnsavedChanges) {
                        toast.current?.show({
                          severity: 'warn',
                          summary: 'Unsaved Changes',
                          detail: 'Please save or cancel your changes before navigating',
                          life: 3000,
                        });
                      } else {
                        navigate(`/fees/edit/${e.value}`);
                      }
                    }
                  }}
                  placeholder="Select a fee configuration"
                  filter
                  filterBy="searchText"
                  className="w-full border-1 border-solid border-gray-300"
                  showClear={false}
                  tooltip="Quick navigation to another fee configuration"
                  tooltipOptions={{ position: 'top' }}
                  appendTo="self"
                />
              </div>
            )}

            <div style={{ width: '375px' }}>
              <FormInputText
                control={control}
                errors={errors}
                field="description"
                label="Description"
                placeholder="Enter description"
                className="flex flex-column"
                inputClassName="border-1 border-solid border-gray-300 w-full"
                disabled={isReadOnly}
              />
            </div>

            <div style={{ width: '375px' }}>
              <div className="flex flex-column">
                <div className="flex justify-content-between align-items-center mb-2">
                  <label className="font-medium">Assigned HUD Line</label>
                  <Button
                    label="Manage HUD Lines"
                    icon="pi pi-external-link"
                    className="p-button-link p-button-sm"
                    style={{ padding: '0', height: 'auto' }}
                    onClick={() => navigate(contextVersionId ? `/fees/hud-lines?versionId=${contextVersionId}` : '/fees/hud-lines')}
                  />
                </div>
                <Tooltip target=".hud-line-dropdown" />
                <div className="hud-line-dropdown" data-pr-tooltip="Assign another HUD Line to this fee configuration" data-pr-position="top">
                  <FormDropdown
                    control={control}
                    errors={errors}
                    field="feeHudLineId"
                    label=""
                    options={hudLineOptions}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select HUD Line"
                    className="flex flex-column"
                    inputClassName="border-1 border-solid border-gray-300 w-full"
                    showFilter={!isReadOnly}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-column gap-2">
              <div className="flex justify-between align-items-center">
                <label className="font-medium">Rules</label>
                {!isReadOnly && (
                  <Button
                    type="button"
                    label="+ Add Rule"
                    className="p-button-sm p-button-primary"
                    onClick={() => handleOpenRuleDialog()}
                  />
                )}
              </div>

              <DataTable
                value={formRules}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 25, 50, 100]}
                emptyMessage="At least one rule must be defined. Click 'Add Rule' to begin."
                className="p-datatable-sm p-datatable-gridlines"
                resizableColumns
                columnResizeMode="fit"
              >
                <Column
                  field="description"
                  header="Description"
                  body={(row: FeeRuleViewModel) => (
                    <>
                      <Tooltip target={`.rule-desc-${row.ruleId}`} />
                      <div
                        className={`rule-desc-${row.ruleId}`}
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          cursor: 'default',
                        }}
                        data-pr-tooltip={row.description || '-'}
                        data-pr-position="top"
                      >
                        {row.description || '-'}
                      </div>
                    </>
                  )}
                  style={{ minWidth: '150px', maxWidth: '220px', verticalAlign: 'top' }}
                />
                <Column
                  field="ruleType"
                  header="Type"
                  body={(row: FeeRuleViewModel) => row.ruleType || '-'}
                  style={{ width: '100px', minWidth: '100px', maxWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}
                />
                <Column
                  header="Condition"
                  body={(row: FeeRuleViewModel) => (
                    <div
                      onClick={() => {
                        setSelectedExpression(row.expression);
                        setIsExpressionDialogVisible(true);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <ExpressionPreview expression={row.expression} />
                    </div>
                  )}
                  style={{ minWidth: '200px', maxWidth: '400px', verticalAlign: 'top' }}
                />
                <Column
                  header="Fee Amount"
                  body={(row: FeeRuleViewModel) => {
                    if (!row.settingData || row.settingData.length === 0) {
                      return '-';
                    }

                    const formatValueForField = (fieldId: string, raw: string) => {
                      const fieldLower = fieldId.toLowerCase();
                      if (fieldLower.includes('feeamount')) return `$ ${raw}`;
                      if (fieldLower.includes('percentage')) return `${raw} %`;
                      return raw;
                    };

                    const formatDate = (iso: string | null) => {
                      if (!iso) return '';
                      return new Date(iso).toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: '2-digit',
                      });
                    };

                    const formatDatePlusOne = (iso: string | null) => {
                      if (!iso) return '';
                      const d = new Date(iso);
                      d.setDate(d.getDate() + 1);
                      return d.toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: '2-digit',
                      });
                    };

                    return (
                      <div
                        style={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          lineHeight: '1.5',
                        }}
                      >
                        {row.settingData.map((sd, index) => {
                          const fieldId = sd.fieldId || '';
                          const value = sd.value || '';
                          const formattedValue = formatValueForField(fieldId, value);
                          const history = (sd.dateValues || [])
                            .slice()
                            .sort((a, b) => {
                              const da = a.date ? new Date(a.date).getTime() : 0;
                              const db = b.date ? new Date(b.date).getTime() : 0;
                              return db - da;
                            });
                          const mostRecentHistoryDate = history[0]?.date || null;
                          const isDateBased = !!sd.isDateBased;

                          if (!isDateBased) {
                            return (
                              <div
                                key={index}
                                style={{
                                  paddingLeft: '1.5rem',
                                  textIndent: '-1.5rem',
                                }}
                              >
                                <strong>{fieldId}:</strong> {formattedValue}
                              </div>
                            );
                          }

                          const asOfLabel = mostRecentHistoryDate
                            ? `as of ${formatDatePlusOne(mostRecentHistoryDate)}`
                            : null;
                          const hasTopMeta = asOfLabel || sd.dateFieldPath;

                          return (
                            <div key={index} className="mb-2">
                              <div>
                                <strong>{fieldId}:</strong> {formattedValue || '(not set)'}
                                {hasTopMeta && (
                                  <span>
                                    {' ('}
                                    {asOfLabel}
                                    {asOfLabel && sd.dateFieldPath ? ' ' : ''}
                                    {sd.dateFieldPath && (
                                      <>[<em>{sd.dateFieldPath}</em>]</>
                                    )}
                                    {')'}
                                  </span>
                                )}
                              </div>
                              {history.map((entry, hi) => {
                                const olderDate = hi < history.length - 1 ? history[hi + 1].date : null;
                                const formattedDate = formatDate(entry.date);
                                const formattedOlder = formatDate(olderDate);
                                const range = olderDate
                                  ? `after ${formattedOlder} and on or before ${formattedDate}`
                                  : `on or before ${formattedDate}`;
                                return (
                                  <div key={hi} style={{ paddingLeft: '0.75rem' }}>
                                    - {formatValueForField(fieldId, entry.value || '') || '(no value)'} ({range})
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }}
                  style={{ minWidth: '150px', maxWidth: '300px', verticalAlign: 'top' }}
                />
                {!isReadOnly && (
                  <Column
                    header="Actions"
                    style={{ width: '8rem', minWidth: '8rem' }}
                    body={(row: FeeRuleViewModel) => (
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          icon="pi pi-pencil"
                          className="p-button-text p-button-sm"
                          onClick={() => handleOpenRuleDialog(row)}
                          tooltip="Edit"
                          tooltipOptions={{ position: 'top' }}
                        />
                        <Button
                          type="button"
                          icon="pi pi-clone"
                          className="p-button-text p-button-sm"
                          onClick={() => handleCopyRule(row)}
                          tooltip="Copy"
                          tooltipOptions={{ position: 'top' }}
                        />
                        <Button
                          type="button"
                          icon="pi pi-trash"
                          className="p-button-text p-button-sm p-button-danger"
                          onClick={(e) => handleDeleteRule(e, row.ruleId)}
                          tooltip="Delete"
                          tooltipOptions={{ position: 'top' }}
                        />
                      </div>
                    )}
                  />
                )}
              </DataTable>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              {isReadOnly ? (
                <Button
                  type="button"
                  label="Done"
                  className="p-button-primary"
                  onClick={handleDone}
                />
              ) : (
                <>
                  <Button
                    type="button"
                    label="Cancel"
                    className="p-button-secondary"
                    onClick={handleCancel}
                  />
                  <Button
                    type="submit"
                    label="Save"
                    className="p-button-primary"
                    disabled={isSaving || (!isCreateMode && !hasUnsavedChanges) || !isFormValid}
                    loading={isSaving}
                  />
                </>
              )}
            </div>
          </div>
        </form>
      </AppCard>

      <Dialog
        visible={showUnsavedDialog}
        onHide={handleCancelNavigation}
        header="Unsaved Changes"
        modal
        style={{ width: '450px' }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Stay"
              onClick={handleCancelNavigation}
              className="p-button-secondary"
            />
            <Button
              label="Leave"
              onClick={handleConfirmNavigation}
              className="p-button-primary"
            />
          </div>
        }
      >
        <p>Leaving this page will discard any unsaved changes.</p>
      </Dialog>

      <FeeRuleEditorDialog
        rule={selectedRule}
        visible={isRuleDialogVisible}
        onClose={handleCloseRuleDialog}
        onSave={handleSaveRule}
        ruleType={null}
      />

      <ExpressionDialog
        visible={isExpressionDialogVisible}
        onHide={() => setIsExpressionDialogVisible(false)}
        expression={selectedExpression}
      />
    </AppPage>
  );
};

